import { atom } from "jotai";
import { ZslApi } from "@zapier/service-providers";
import { TEMP } from "@zapier/canvas-constants";
import { z } from "zod";
import { getConnectedEdges, Node } from "@xyflow/react";

import {
  nodesAtom,
  edgesAtom,
  saveCanvasSetter,
  nodeAtomFamily,
  tablesAppAtom,
  interfacesAppAtom,
  SuggestedNodeData,
} from "../index";

import { isAcceptingSuggestionStateAtom } from "./states";

export const replaceSuggestedWithAcceptedSetter = atom(
  null,
  (
    get,
    set,
    {
      nodeId,
      executeZslResponse,
      type,
    }: {
      nodeId: string;
      executeZslResponse: z.infer<typeof ZslApi.executeZslResponseSchema>;
      type?: "table" | "interface" | "zap";
    },
  ) => {
    const originalNodes = get(nodesAtom);
    const edges = get(edgesAtom);
    const suggestedNode = get(
      nodeAtomFamily(nodeId),
    ) as Node<SuggestedNodeData>;
    if (!suggestedNode) return;

    const tablesApp = get(tablesAppAtom);
    const interfacesApp = get(interfacesAppAtom);

    let acceptedNode;
    const nodeType = type || suggestedNode.data.suggestedType;
    // Extract the node data based on type
    if (nodeType === "table") {
      const tableId = Object.keys(executeZslResponse.tables)[0];
      const tableName = executeZslResponse.tables[tableId].name;

      acceptedNode = {
        id: nodeId,
        type: "default",
        data: {
          attributionSourceType: "AI_NEXT_STEP",
          tableId: tableId,
          app: "Zapier Tables",
          label: tableName,
          services: [
            {
              currentImplementation: {
                id: tablesApp?.current_implementation_id,
              },
            },
          ],
        },
        position: suggestedNode.position,
      };
    } else if (nodeType === "interface") {
      const interfaceId = Object.keys(executeZslResponse.interfaces)[0];
      const pageId = Object.keys(
        executeZslResponse.interfaces[interfaceId].pages,
      )[0];
      const pageTitle =
        executeZslResponse.interfaces[interfaceId].pages[pageId].title;
      acceptedNode = {
        id: nodeId,
        type: "default",
        data: {
          attributionSourceType: "AI_NEXT_STEP",
          label: pageTitle,
          projectId: interfaceId,
          pageId: pageId,
          app: "Zapier Interfaces",
          services: [
            {
              currentImplementation: {
                id: interfacesApp?.current_implementation_id,
              },
            },
          ],
        },
        position: suggestedNode.position,
      };
    } else if (nodeType === "zap") {
      const zapId = Object.keys(executeZslResponse.zaps)[0];
      const zapTitle = executeZslResponse.zaps[zapId].title;
      acceptedNode = {
        id: nodeId,
        type: "zap",
        data: {
          attributionSourceType: "AI_NEXT_STEP",
          label: zapTitle,
          zapId: zapId,
        },
        position: suggestedNode.position,
      };
    }

    if (!acceptedNode) return;

    const updatedNodes = [
      ...originalNodes.filter((node) => node.id !== nodeId),
      acceptedNode,
    ];

    set(nodesAtom, updatedNodes);

    // Find all edges connected to the node being replaced
    const connectedEdges = getConnectedEdges([suggestedNode], edges);

    // Update edges by removing TEMP className
    const updatedEdges = edges.map((edge) => {
      if (connectedEdges.includes(edge) && edge.className === TEMP) {
        // Create new edge without the TEMP className
        const { className, ...restEdge } = edge;
        return restEdge;
      }
      return edge;
    });

    set(edgesAtom, updatedEdges);

    set(isAcceptingSuggestionStateAtom, false);

    // Save both updated nodes and edges
    set(saveCanvasSetter, {
      nodes: updatedNodes,
      edges: updatedEdges,
    });
  },
);

replaceSuggestedWithAcceptedSetter.debugLabel =
  "replaceSuggestedWithAcceptedSetter";
